/**
 * useHomeENDemoPeopleDeskLogic
 *
 * Pure logic hook — state machine, orbital bridge, actions.
 * Use the companion component for rendering.
 *
 * @generated from schema
 * @trait HomeENDemoPeopleDesk
 */

import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { useOrbitalBridge } from '@/hooks/useOrbitalBridge';
import type { EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import type { EventResponse, HomeENDemoPeopleLeave, HomeENDemoPeopleDeskEventPayloadMap } from '@app/shared';
import { useUIEvents, claimDelivery } from '@almadar/ui/hooks';
import { registerTraitSnapshot, type TraitStateSnapshot } from '@almadar/ui/lib';
import { createCommandSendPump } from '@almadar/ui/lib';
import type { BusEvent } from '@almadar/core';

// ============================================================================
// State Machine
// ============================================================================

export type HomeENDemoPeopleDeskState =
  | 'idle';

export type HomeENDemoPeopleDeskEvent =
  | 'INIT'
  | 'LEAVES_CHANGED'
  | 'APPROVE_MAJA'
  | 'ADD_SARA'
  | 'APPROVE_LUKA'
  | 'APPROVE_SARA'
  | 'DESK_RESET'
  | 'LEAVES_LOADED'
  | 'TOUR_STEP'
  | 'TOUR_RESET';

type HomeENDemoPeopleDeskSlot = never;
interface HomeENDemoPeopleDeskInternalState {
  machineState: HomeENDemoPeopleDeskState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: HomeENDemoPeopleDeskEvent | null;
  slotWriter: Partial<Record<HomeENDemoPeopleDeskSlot, HomeENDemoPeopleDeskEvent | null>>;
  loading: boolean;
  pendingEvent: HomeENDemoPeopleDeskEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<HomeENDemoPeopleLeave>;
}

type HomeENDemoPeopleDeskAction =
  | { type: 'EVENT_SUCCESS'; event: HomeENDemoPeopleDeskEvent; newState: string; data: Record<string, EntityRow[]>; payload?: EventPayload; fields?: Record<string, EventPayloadValue | undefined>; renderBearing?: boolean; renderWrite?: HomeENDemoPeopleDeskSlot[]; renderClear?: HomeENDemoPeopleDeskSlot[] }
  | { type: 'EVENT_ERROR'; error: string }
  | { type: 'RESET_ERROR' }
  | { type: 'START_EVENT'; event: HomeENDemoPeopleDeskEvent; from: string }
  | { type: 'END_EVENT' }
  | { type: 'SET_FIELD'; field: keyof HomeENDemoPeopleLeave & string; value: EventPayloadValue | undefined }
  | { type: 'TICK'; updates: Record<string, EventPayloadValue | undefined>; collection: string };

function homeENDemoPeopleDeskReducer(
  state: HomeENDemoPeopleDeskInternalState,
  action: HomeENDemoPeopleDeskAction
): HomeENDemoPeopleDeskInternalState {
  switch (action.type) {
    case 'START_EVENT':
      return { ...state, loading: true, pendingEvent: action.event, pendingFrom: action.from };
    case 'END_EVENT':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null };
    case 'EVENT_SUCCESS':
      return {
        machineState: action.newState as HomeENDemoPeopleDeskState,
        data: { ...state.data, ...action.data },
        lastPayload: action.renderBearing ? (action.payload ?? state.lastPayload) : state.lastPayload,
        lastEvent: action.renderBearing ? action.event : state.lastEvent,
        slotWriter: {
          ...state.slotWriter,
          ...Object.fromEntries((action.renderWrite ?? []).map((s) => [s, action.event] as const)),
          ...Object.fromEntries((action.renderClear ?? []).map((s) => [s, null] as const)),
        },
        loading: false,
        pendingEvent: null,
        pendingFrom: null,
        error: null,
        fields: { ...state.fields, ...(action.fields ?? {}) } as Partial<HomeENDemoPeopleLeave>,
      };
    case 'EVENT_ERROR':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null, error: action.error };
    case 'RESET_ERROR':
      return { ...state, error: null };
    case 'SET_FIELD': {
      // Back-compat for non-codegen consumers that still dispatch
      // SET_FIELD directly. Codegen folds field updates into
      // EVENT_SUCCESS so transitions commit in a single render.
      return {
        ...state,
        fields: { ...state.fields, [action.field]: action.value } as Partial<HomeENDemoPeopleLeave>,
      };
    }
    case 'TICK': {
      // Batch-update entity fields for tick effects. A [runtime]-linked
      // trait's row lives in the `fields` mirror (transitions write it,
      // the view reads it), so updates ALWAYS merge there; the
      // `data` collection row is updated too when one exists.
      const tickFields = { ...state.fields, ...action.updates } as Partial<HomeENDemoPeopleLeave>;
      const arr = state.data[action.collection];
      const target = arr?.[0];
      if (!target) return { ...state, fields: tickFields };
      const updated = { ...target, ...action.updates } as EntityRow;
      return {
        ...state,
        data: { ...state.data, [action.collection]: [updated, ...arr.slice(1)] },
        fields: tickFields,
      };
    }
    default:
      return state;
  }
}

const initialState: HomeENDemoPeopleDeskInternalState = {
  machineState: 'idle',
  data: {},
  lastPayload: undefined,
  lastEvent: null,
  slotWriter: {},
  loading: false,
  pendingEvent: null,
  pendingFrom: null,
  error: null,
  fields: {},
};

// ============================================================================
// Hook
// ============================================================================

export interface HomeENDemoPeopleDeskConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
}

export interface HomeENDemoPeopleDeskLogicReturn {
  state: HomeENDemoPeopleDeskState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: HomeENDemoPeopleDeskEvent | null;
  slotWriter: Partial<Record<HomeENDemoPeopleDeskSlot, HomeENDemoPeopleDeskEvent | null>>;
  loading: boolean;
  pendingEvent: HomeENDemoPeopleDeskEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<HomeENDemoPeopleLeave>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof HomeENDemoPeopleDeskEventPayloadMap]: (payload?: HomeENDemoPeopleDeskEventPayloadMap[K]) => Promise<EventResponse> };
  now: number;
  config?: HomeENDemoPeopleDeskConfig;
}

const __CASCADE_EVENTS = new Set<string>(['ADD_SARA', 'APPROVE_LUKA', 'APPROVE_MAJA', 'APPROVE_SARA', 'DESK_RESET', 'TOUR_RESET', 'TOUR_STEP']);

const __LEG_EVENTS: ReadonlySet<string> = new Set(['ADD_SARA', 'APPROVE_LUKA', 'APPROVE_MAJA', 'APPROVE_SARA', 'DESK_RESET', 'INIT', 'LEAVES_CHANGED']);

export function useHomeENDemoPeopleDeskLogic(config?: Partial<HomeENDemoPeopleDeskConfig>): HomeENDemoPeopleDeskLogicReturn {
  const mergedConfig = config;

  // Orbital bridge: event bus + server communication
  const { sendEvent, eventBus } = useOrbitalBridge({ basePath: '/api/home-en-demo-people-desk', orbitalName: 'HomeEN', traitName: 'HomeENDemoPeopleDesk' });

  // Internal state with reducer
  const [internalState, dispatchReducer] = useReducer(homeENDemoPeopleDeskReducer, initialState);
  const { machineState: state, data, lastPayload, lastEvent, slotWriter, loading, pendingEvent, pendingFrom, error, fields: _initialFields } = internalState;
  const fields = _initialFields;
  const internalStateRef = useRef(internalState);
  internalStateRef.current = internalState;

  const cascadeReceivedRef = useRef<TraitStateSnapshot['cascadeReceived']>([]);

  const machineStateRef = useRef(state);
  machineStateRef.current = state;

  const fieldsRef = useRef<EntityRow>({ ...fields });
  fieldsRef.current = { ...fields };
  const commandPump = useMemo(() => createCommandSendPump(), []);

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  const __lastNow = useRef<number>(Date.now());
  const now = __lastNow.current;

  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, _payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const entity: EntityRow[] = liveData['HomeENDemoPeopleLeave'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};

    if (fromState === 'idle' && event === 'INIT') {
      // idle → idle (on INIT)
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'LEAVES_CHANGED') {
      // idle → idle (on LEAVES_CHANGED)
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'APPROVE_MAJA') {
      // idle → idle (on APPROVE_MAJA)
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'ADD_SARA') {
      // idle → idle (on ADD_SARA)
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'APPROVE_LUKA') {
      // idle → idle (on APPROVE_LUKA)
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'APPROVE_SARA') {
      // idle → idle (on APPROVE_SARA)
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'DESK_RESET') {
      // idle → idle (on DESK_RESET)
      _newState = 'idle';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  // Used by dispatch to skip lastPayload/lastEvent updates for
  // transitions whose only effects are server-side or set/emit —
  // those don't change the rendered JSX, so the view should keep
  // the prior frame's payload until a render-bearing transition
  // (e.g. fetch.emit.success cascade) lands.
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: HomeENDemoPeopleDeskSlot[]; clear: HomeENDemoPeopleDeskSlot[] } => {
    return { write: [], clear: [] };
  }, []);

  /**
   * Dispatch an event through the orbital bridge.
   * Bridge emits lifecycle events on the bus, forwards to server.
   * Client effects are transpiled at compile time (Phase 4).
   */
  const dispatch = useCallback(async (event: string, __rawPayload?: EventPayload): Promise<EventResponse> => {
    const now = Date.now();
    __lastNow.current = now;
    const payload: EventPayload | undefined =
      __rawPayload !== undefined && ('entityId' in __rawPayload || 'id' in __rawPayload)
        ? __rawPayload
        : config?.entityId !== undefined
          ? { ...(__rawPayload ?? {}), entityId: config.entityId }
          : __rawPayload;
    const prevState = machineStateRef.current;
    // T9 optimistic dispatch: the fields mirror IS the [runtime] entity
    // row, so guard + to-state resolve locally on the same data the
    // server would read. Commit first, POST after; reconcile below.
    const liveData: Record<string, EntityRow[]> = { 'HomeENDemoPeopleLeave': [fieldsRef.current as EntityRow] };
    const _optimistic = executeTransitionEffects(prevState, event, liveData, payload);
    if (_optimistic.newState === undefined) {
      // Guard rejected or no transition defined — mirrors the server's
      // rejection, without the round trip.
      const errorMsg = `No transition defined for ${event} from ${prevState} (or guard rejected)`;
      dispatchReducer({ type: 'EVENT_ERROR', error: errorMsg });
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const _renderSlots = getRenderUiSlots(prevState, event);
    const _renderBearing = _renderSlots.write.length > 0 || _renderSlots.clear.length > 0;
    // Snapshot the touched fields' prior values for a reconcile rollback.
    const _snapshot: Record<string, EventPayloadValue | undefined> = {};
    for (const _k of Object.keys(_optimistic.fields)) { _snapshot[_k] = fieldsRef.current[_k]; }
    dispatchReducer({
      type: 'EVENT_SUCCESS',
      event: event as HomeENDemoPeopleDeskEvent,
      newState: _optimistic.newState,
      data: {},
      payload,
      fields: _optimistic.fields,
      renderBearing: _renderBearing,
      renderWrite: _renderSlots.write,
      renderClear: _renderSlots.clear,
    });
    if (__CASCADE_EVENTS.has(event)) {
      cascadeReceivedRef.current.push({ event, ...(payload !== undefined ? { payload } : {}), timestamp: Date.now() });
    }
    machineStateRef.current = _optimistic.newState as typeof machineStateRef.current;
    fieldsRef.current = { ...fieldsRef.current, ..._optimistic.fields } as EntityRow;
    for (const _emit of _optimistic.emits) _emit();

    if (__LEG_EVENTS.has(event)) {
    // Server leg: ordered by the pump, never blocking the drain.
    commandPump.enqueue(async () => {
      try {
        const response = await sendEvent(event, payload, prevState);
        if (response.success) {
          // Agreement — merge any fetch/persist results the local commit
          // couldn't supply (same reducer action, deferred leg).
          if (response.data && Object.keys(response.data).length > 0) {
            dispatchReducer({
              type: 'EVENT_SUCCESS',
              event: event as HomeENDemoPeopleDeskEvent,
              newState: machineStateRef.current,
              data: response.data,
              renderBearing: false,
            });
          }
        } else {
          // Rejection or state disagreement — roll back to the
          // pre-dispatch snapshot; the server stays authoritative.
          dispatchReducer({
            type: 'EVENT_SUCCESS',
            event: event as HomeENDemoPeopleDeskEvent,
            newState: prevState,
            data: {},
            fields: _snapshot,
            renderBearing: false,
          });
          machineStateRef.current = prevState;
          fieldsRef.current = { ...fieldsRef.current, ..._snapshot } as EntityRow;
          dispatchReducer({ type: 'EVENT_ERROR', error: response.error ?? `server state mismatch: expected ${_optimistic.newState}, got ${response.states?.['HomeENDemoPeopleDesk'] ?? '(unchanged)'}` });
        }
      } catch (err) {
        // Pump isolates the rejection; surface it without rolling
        // back — local state may simply be ahead of a reconnecting server.
        dispatchReducer({ type: 'EVENT_ERROR', error: err instanceof Error ? err.message : 'Network error' });
      }
    });
    }
    return { success: true, transitioned: true, states: { 'HomeENDemoPeopleDesk': _optimistic.newState }, emittedEvents: [] } as EventResponse;
  }, [sendEvent, executeTransitionEffects, commandPump, config?.entityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof HomeENDemoPeopleDeskEventPayloadMap]: (payload?: HomeENDemoPeopleDeskEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'LEAVES_CHANGED': () => dispatch('LEAVES_CHANGED'),
    'APPROVE_MAJA': () => dispatch('APPROVE_MAJA'),
    'ADD_SARA': () => dispatch('ADD_SARA'),
    'APPROVE_LUKA': () => dispatch('APPROVE_LUKA'),
    'APPROVE_SARA': () => dispatch('APPROVE_SARA'),
    'DESK_RESET': () => dispatch('DESK_RESET'),
    'LEAVES_LOADED': (payload?: HomeENDemoPeopleDeskEventPayloadMap['LEAVES_LOADED']) => dispatch('LEAVES_LOADED', payload),
  };

  useUIEvents(enqueueEvent, 'HomeEN.HomeENDemoPeopleDesk', ['INIT', 'LEAVES_CHANGED', 'APPROVE_MAJA', 'ADD_SARA', 'APPROVE_LUKA', 'APPROVE_SARA', 'DESK_RESET'] as const, eventBus);

  // Verifier snapshot registration (VG Foundation 1)
  useEffect(() => {
    const unregister = registerTraitSnapshot('HomeENDemoPeopleDesk', (): TraitStateSnapshot => ({
      traitName: 'HomeENDemoPeopleDesk',
      currentState: internalStateRef.current.machineState,
      states: ['idle'],
      events: ['INIT', 'LEAVES_CHANGED', 'APPROVE_MAJA', 'ADD_SARA', 'APPROVE_LUKA', 'APPROVE_SARA', 'DESK_RESET', 'LEAVES_LOADED'],
      data: internalStateRef.current.data,
      lastPayload: internalStateRef.current.lastPayload,
      lastEventDispatched: internalStateRef.current.lastEvent !== null
        ? {
            event: internalStateRef.current.lastEvent,
            ...(internalStateRef.current.lastPayload !== undefined
              ? { payload: internalStateRef.current.lastPayload }
              : {}),
            timestamp: Date.now(),
          }
        : undefined,
      cascadeReceived: cascadeReceivedRef.current,
    }));
    return unregister;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cross-trait event listeners (gap #13: qualified bus keys)
  useEffect(() => {
    // Listen for 'TOUR_STEP' (from HomeENDemoPeopleTour) → trigger 'APPROVE_MAJA'
    const unsub_tour_step_from_homeendemopeopletour_to_approve_maja = eventBus.on('UI:HomeEN.HomeENDemoPeopleTour.TOUR_STEP', (event: BusEvent) => {
      if (!(((event.payload as EventPayload | undefined)?.event === "APPROVE_MAJA"))) return;
      if (!claimDelivery(event.source, 'HomeEN.HomeENDemoPeopleDesk', 'APPROVE_MAJA')) return;
      enqueueEvent('APPROVE_MAJA', event.payload);
    });
    // Listen for 'TOUR_STEP' (from HomeENDemoPeopleTour) → trigger 'ADD_SARA'
    const unsub_tour_step_from_homeendemopeopletour_to_add_sara = eventBus.on('UI:HomeEN.HomeENDemoPeopleTour.TOUR_STEP', (event: BusEvent) => {
      if (!(((event.payload as EventPayload | undefined)?.event === "ADD_SARA"))) return;
      if (!claimDelivery(event.source, 'HomeEN.HomeENDemoPeopleDesk', 'ADD_SARA')) return;
      enqueueEvent('ADD_SARA', event.payload);
    });
    // Listen for 'TOUR_STEP' (from HomeENDemoPeopleTour) → trigger 'APPROVE_LUKA'
    const unsub_tour_step_from_homeendemopeopletour_to_approve_luka = eventBus.on('UI:HomeEN.HomeENDemoPeopleTour.TOUR_STEP', (event: BusEvent) => {
      if (!(((event.payload as EventPayload | undefined)?.event === "APPROVE_LUKA"))) return;
      if (!claimDelivery(event.source, 'HomeEN.HomeENDemoPeopleDesk', 'APPROVE_LUKA')) return;
      enqueueEvent('APPROVE_LUKA', event.payload);
    });
    // Listen for 'TOUR_STEP' (from HomeENDemoPeopleTour) → trigger 'APPROVE_SARA'
    const unsub_tour_step_from_homeendemopeopletour_to_approve_sara = eventBus.on('UI:HomeEN.HomeENDemoPeopleTour.TOUR_STEP', (event: BusEvent) => {
      if (!(((event.payload as EventPayload | undefined)?.event === "APPROVE_SARA"))) return;
      if (!claimDelivery(event.source, 'HomeEN.HomeENDemoPeopleDesk', 'APPROVE_SARA')) return;
      enqueueEvent('APPROVE_SARA', event.payload);
    });
    // Listen for 'TOUR_RESET' (from HomeENDemoPeopleTour) → trigger 'DESK_RESET'
    const unsub_tour_reset_from_homeendemopeopletour_to_desk_reset = eventBus.on('UI:HomeEN.HomeENDemoPeopleTour.TOUR_RESET', (event: BusEvent) => {
      if (!claimDelivery(event.source, 'HomeEN.HomeENDemoPeopleDesk', 'DESK_RESET')) return;
      enqueueEvent('DESK_RESET', event.payload);
    });
    return () => {
      if (typeof unsub_tour_step_from_homeendemopeopletour_to_approve_maja === 'function') unsub_tour_step_from_homeendemopeopletour_to_approve_maja();
      if (typeof unsub_tour_step_from_homeendemopeopletour_to_add_sara === 'function') unsub_tour_step_from_homeendemopeopletour_to_add_sara();
      if (typeof unsub_tour_step_from_homeendemopeopletour_to_approve_luka === 'function') unsub_tour_step_from_homeendemopeopletour_to_approve_luka();
      if (typeof unsub_tour_step_from_homeendemopeopletour_to_approve_sara === 'function') unsub_tour_step_from_homeendemopeopletour_to_approve_sara();
      if (typeof unsub_tour_reset_from_homeendemopeopletour_to_desk_reset === 'function') unsub_tour_reset_from_homeendemopeopletour_to_desk_reset();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventBus, dispatch]);

  return {
    state,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading,
    pendingEvent,
    pendingFrom,
    error,
    fields,
    dispatch,
    actions,
    now,
    config: mergedConfig,
  };
}
